/**
 * QRDisplayPage — full-screen, mobile-first page for a user holding the
 * `display_qr` additive capability (typically a receptionist manning the
 * front desk). Fetches the current QR token for the active branch and
 * renders the code full width with the auto-refresh countdown honouring
 * the per-branch `qr_auto_regen_secs` setting.
 *
 * Defense in depth (iter-58):
 *   1. Requires `display_qr` in the user's roles array.
 *   2. Requires the active branch to have `attendance.attendance_method === "qr"`.
 *   3. Requires a specific branch (never "all").
 * If any check fails we render an "Access Not Available" card. The backend
 * enforces the same rules on /api/qr/branch/{id}/token so a direct URL /
 * back-button entry cannot bypass this.
 */
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, QrCode, Prohibit } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import { ShowQRPanel } from "@/components/attendance/QRCheckinDialog";

export default function QRDisplayPage() {
  const { activeBranch, branches, user } = useAuth();
  const navigate = useNavigate();
  const branch = (branches || []).find(b => b.id === activeBranch);

  const hasCap = (user?.roles || []).includes("display_qr")
                  || (user?.roles || []).includes("owner")
                  || (user?.roles || []).includes("admin");
  const branchIsQr = branch?.attendance?.attendance_method === "qr";
  const accessDenied = !hasCap || activeBranch === "all" || !branch || !branchIsQr;

  return (
    <div className="min-h-[calc(100vh-4rem)] flex flex-col bg-background" data-testid="qr-display-page">
      <div className="flex items-center gap-3 px-4 py-3 border-b bg-card">
        <Button
          variant="ghost" size="icon" className="h-8 w-8"
          onClick={() => navigate(-1)}
          aria-label="Back"
          data-testid="qr-display-back"
        >
          <ArrowLeft size={18} />
        </Button>
        <div className="flex items-center gap-2 flex-1">
          <QrCode size={20} weight="duotone" />
          <div className="min-w-0">
            <div className="text-sm font-semibold truncate">Display QR</div>
            <div className="text-[11px] text-muted-foreground truncate">
              {branch?.name || "Select a branch first"}
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        {accessDenied ? (
          <AccessNotAvailable
            title="Access Not Available"
            reason={
              !hasCap
                ? "Your account doesn't have the Display QR permission. Ask an admin to grant it under Settings → Users."
                : activeBranch === "all"
                ? "Pick a specific branch from the top bar."
                : !branch
                ? "The selected branch could not be found."
                : "This branch is not on QR attendance. Ask an admin to enable QR under Settings → Branches → Edit."
            }
          />
        ) : (
          <div className="w-full max-w-sm mx-auto">
            <ShowQRPanel branchId={branch.id} />
          </div>
        )}
      </div>
    </div>
  );
}

function AccessNotAvailable({ title, reason }) {
  return (
    <div className="max-w-sm w-full text-center space-y-3 border border-dashed rounded-lg p-6" data-testid="qr-access-denied">
      <div className="mx-auto h-11 w-11 rounded-full bg-muted flex items-center justify-center">
        <Prohibit size={20} weight="duotone" className="text-muted-foreground" />
      </div>
      <div className="font-display font-semibold text-lg">{title}</div>
      <p className="text-sm text-muted-foreground">{reason}</p>
    </div>
  );
}
